import { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import DropdownMenu from '../../shared/dropdownMenu';
import PageContainer from '../../shared/pageContainer';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import CustomFormDrawer from '../../shared/customFormDrawer';
import ToggleButtonSwitch from '../../shared/toggleButtonSwitch';
import Filter from '../../shared/filter';
import Search from '../../shared/search';
import CustomButton from '../../shared/customButton';
import CandidatesList from '../../shared/candidatesList';
import AlertDialog from '../../shared/alertDialog';
import NotificationCard from '../../shared/notificationCard';
import CandidateInfo from '../../shared/candidateInfo';
import { ReactComponent as NeutralIcon } from '../../../assets/icons/sentiment_neutral_dark.svg';
import { ReactComponent as TimerIcon } from '../../../assets/icons/timer.svg';
import {
  api_add_candidate_to_step,
  api_candidate,
  api_candidate_comment,
  api_candidate_comments,
  api_candidate_disqualify,
  api_candidate_emails,
  api_candidate_qualify,
  api_candidate_review,
  api_candidate_reviews,
  API_CANDIDATES,
  API_CANDIDATES_FROM_CV,
  api_candidates_in_job,
  api_job_team,
  api_selection_step_candidate,
} from '../../../constants/apiRoutes';
import { CANDIDATE_ORDERING_DEFAULT } from '../../../constants/job';
import {
  APP_PAGES,
  JOB_DETAILS_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
  PARAMS,
  TIMELINE_DEFAULT_PARAMS,
} from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { jobsSelector } from '../../../store/selectors/jobsSelector';
import { jobDetailsSelector } from '../../../store/selectors/jobDetailsSelector';
import { clearAllUsers, getAllUsers } from '../../../store/modules/people';
import {
  clearComments,
  clearJob,
  clearReviews,
  clearTimeline,
  getCandidateComments,
  getCandidateEmails,
  getCandidateReviews,
  getCandidateTimeline,
  getJob,
} from '../../../store/modules/jobDetails';
import {
  clearAllCandidates,
  clearCandidates,
  getAllCandidates,
  getCandidatesInStep,
  updateCandidateLastUpdate,
} from '../../../store/modules/jobs';
import {
  getObjectToNumberArray,
  isArray,
  isArrayEmpty,
  isObject,
  isObjectEmpty,
  trimString,
} from '../../../utility/helpers';
import { hasAuthPermissionsCheck } from '../../../utility/permissions';
import {
  getEmptyStepMessage,
  hasNextPage,
  parseDuplicateParameters,
  parseQueryParams,
  showErrorMessage,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { isEmpty } from '../../../utility/validation';
import { useTranslations } from '../../../utility/useTranslations';
import http from '../../../utility/http';
import { CANDIDATE_NAVIGATIONS } from '../../shared/candidateInfo/config';
import {
  GET_SEND_EMAIL_FIELDS,
  getAddCommentFields,
  getAddCommentInitialData,
  getCandidateNavigationItems,
  prepareCandidateOrderingOptions,
  prepareJobDetailsQueryParams,
  getNavItems,
  getReviews,
  getSendEmailInitialData,
  getSwitchItems,
  prepareValidCandidateOrdering,
  getVoteFields,
  getVoteInitialData,
  QUALIFIED_STATUS,
} from './config';
import {
  UPLOAD_CV_INITIAL_DATA,
  getUploadCvFields,
  prepareAddCandidatesFields,
  getCandidateActionItems,
  getCandidateFields,
  prepareHiringTeamFields,
  prepareCandidateInitialData,
  prepareCandidatesInitialData,
  prepareHiringTeamInitialData,
} from '../jobsPage/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr)',
      height: 'calc(100vh - 160px)',
      [breakpoints.up('lg')]: {
        gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)',
      },
      [breakpoints.up('xl')]: {
        gridTemplateColumns: 'minmax(0, 469px) minmax(0, 1fr)',
      },
    },
    pageContainer: {
      padding: spacing(0, 1.5, 0, 4.5),
      [breakpoints.up('xLg')]: {
        padding: spacing(0, 7, 0, 6),
      },
      [breakpoints.up('xl')]: {
        padding: spacing(0, 9, 0, 12),
      },
    },
    pageHeader: {
      paddingRight: spacing(3),
    },
    form: {
      display: 'grid',
      gridColumnGap: 24,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridTemplateAreas: `
      "firstName lastName"
      "professionTitle email"
      "dateOfBirth phone"
      "referral ."
      "cv parseCheckbox"
      "education education"
      "experience experience"
      "skills skills"
      "city address"
      "country ."
    `,
    },
    firstName: {
      gridArea: 'firstName',
    },
    lastName: {
      gridArea: 'lastName',
    },
    email: {
      gridArea: 'email',
    },
    cv: {
      gridArea: 'cv',
    },
    parseCheckbox: {
      gridArea: 'parseCheckbox',
      display: 'flex',
      alignItems: 'center',
    },
    professionTitle: {
      gridArea: 'professionTitle',
    },
    experience: {
      gridArea: 'experience',
    },
    skills: {
      gridArea: 'skills',
    },
    education: {
      gridArea: 'education',
    },
    referral: {
      gridArea: 'referral',
    },
    dateOfBirth: {
      gridArea: 'dateOfBirth',
    },
    phone: {
      gridArea: 'phone',
    },
    address: {
      gridArea: 'address',
    },
    city: {
      gridArea: 'city',
    },
    country: {
      gridArea: 'country',
    },
    drawerContent: {
      height: '100%',
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      marginBottom: 0,
      minHeight: 0,
    },
    picker: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
    candidateList: {
      boxSizing: 'border-box',
      borderRight: `1px solid ${primary.bluish7}`,
      display: 'flex',
      flexDirection: 'column',
      padding: spacing(8, 4, 6, 0),
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      [breakpoints.up('xl')]: {
        padding: spacing(8, 12, 6, 0),
      },
    },
    switch: {
      justifyContent: 'center',
      display: 'flex',
      width: '100%',
      '& > div': {
        width: '100%',
      },
    },
    switchButton: {
      width: '100%',
    },
    searchRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 8,
      marginTop: spacing(4),
      width: '100%',
      minWidth: 0,
    },
    search: {
      minWidth: 0,
      width: 'auto',
      maxWidth: 'none',
    },
    orderingFilter: {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      '& .MuiTextField-root, & .MuiInputBase-root': {
        width: '100%',
        maxWidth: '100%',
      },
      '& .MuiSelect-root': {
        minWidth: 0,
      },
    },
    orderingPaper: {
      minWidth: '0 !important',
    },
    uploadIcon: {
      padding: spacing(2),
    },
    uploadIconWrapper: {
      marginTop: spacing(23.75),
      justifyContent: 'center',
      display: 'flex',
    },
    label: {
      lineHeight: '24px',
    },
    labelWrapper: {
      marginTop: spacing(4),
      justifyContent: 'center',
      display: 'flex',
    },
    uploadButtonWrapper: {
      marginTop: spacing(3),
      display: 'flex',
      justifyContent: 'center',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      minWidth: 0,
    },
    titleWrapper: {
      maxWidth: 'calc(100% - 490px)',
    },
    title: {
      paddingTop: spacing(1),
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    button: {
      fontSize: 16,
      lineHeight: '24px',
      fontFamily: 'ProximaNova-Bold',
      padding: spacing(2, 4, 2, 4),
      minWidth: 'fit-content',
    },
    addMemberButton: {
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: primary.bluish8,
      },
    },
    navigation: {
      paddingBottom: 0,
    },
    candidate: {
      cursor: 'pointer',
    },
    content: {
      '& .ql-container': {
        height: 600,
        borderColor: primary.bluish6,
      },
      '& .ql-toolbar': {
        borderColor: primary.bluish6,
      },
      '& > div': {
        height: 445,
      },
    },
    commentErrorClass: {
      bottom: -220,
    },
    noCandidatesCard: {
      display: 'flex',
      justifyContent: 'center',
      flex: 1,
      marginTop: spacing(6),
    },
    noSelectedCandidateCard: {
      borderRadius: 'unset',
      justifyContent: 'center',
    },
    notificationTitleClass: {
      marginBottom: spacing(2),
    },
    scrollArrow: {
      top: 124,
      [breakpoints.up('xLg')]: {
        top: 121,
      },
    },
    disabledContainer: {
      opacity: '20%',
      cursor: 'default',
      pointerEvents: 'none',
    },
    detailsButton: {
      fontSize: 16,
      lineHeight: '24px',
      fontFamily: 'ProximaNova-Bold',
      height: 40,
      padding: spacing(1, 4, 1, 4),
    },
    dropdownMenu: {
      height: 40,
      '&:hover': {
        height: 40,
      },
    },
  })
);

const JobDetailsPage = ({
  location,
  auth,
  accessControl,
  dispatch,
  navigate,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.JOB_DETAILS);
  const { page: defaultPage } = TIMELINE_DEFAULT_PARAMS;
  const { status: defaultStatus } = JOB_DETAILS_DEFAULT_PARAMS;
  const { SEARCH, CANDIDATE, SELECTION_STEP, PAGE, STATUS, ORDERING } = PARAMS;
  const { jobId, stepId, navSection } = useParams();
  const [searchParams] = useSearchParams();
  const paramsStatus = searchParams?.get('status');
  const paramsSearch = searchParams?.get('search');
  const paramsOrdering = searchParams?.get('ordering');
  const { allUsers } = useSelector(peopleSelector);
  const { candidates, allCandidates } = useSelector(jobsSelector);
  const { job, reviews, comments, emails, timeline } =
    useSelector(jobDetailsSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const [hasLoadMoreTimeline, setHasLoadMoreTimeline] = useState(false);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState(CANDIDATE_ORDERING_DEFAULT);
  const [timelinePage, setTimelinePage] = useState(1);
  const [hiringTeam, setHiringTeam] = useState([]);
  const [candidateReviews, setCandidateReviews] = useState({});
  const [candidatesForJob, setCandidatesForJob] = useState([]);
  const [isHiringTeamDrawerOpened, setIsHiringTeamDrawerOpened] =
    useState(false);
  const [isDisqualifyDialogOpened, setIsDisqualifyDialogOpened] =
    useState(false);
  const [isQualifyDialogOpened, setIsQualifyDialogOpened] = useState(false);
  const [isVoteDrawerOpened, setIsVoteDrawerOpened] = useState(false);
  const [selectedVote, setSelectedVote] = useState({});
  const [
    isAddExistingCandidatesDrawerOpened,
    setIsAddExistingCandidatesDrawerOpened,
  ] = useState(false);
  const [isSendEmailDrawerOpened, setIsSendEmailDrawerOpened] = useState(false);
  const [isAddCommentDrawerOpened, setIsAddCommentDrawerOpened] =
    useState(false);
  const [isDeleteCommentDialogOpened, setIsDeleteCommentDialogOpened] =
    useState(false);
  const [isDeleteVoteDialogOpened, setIsDeleteVoteDialogOpened] =
    useState(false);
  const [isEditCandidate, setIsEditCandidate] = useState(false);
  const [isDeleteCandidateDialogOpened, setIsDeleteCandidateDialogOpened] =
    useState(false);
  const [selectedComment, setSelectedComment] = useState({});
  const [isQualifiedView, setIsQualifiedView] = useState(false);
  const [
    isAddManuallyCandidatesDrawerOpened,
    setIsAddManuallyCandidatesDrawerOpened,
  ] = useState(false);
  const [isUploadCvDrawerOpened, setIsUploadCvDrawerOpened] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState({});
  const selectCandidateRequestRef = useRef(0);

  const isArchivedJob = isEmpty(job.archived_at);
  const hasCandidates = isArray(candidates) && !isArrayEmpty(candidates);
  const [canEdit] = hasAuthPermissionsCheck(
    [PERMISSION_CODES.RECRUITMENT_EDIT],
    accessControl
  );
  const candidatesList = hasCandidates
    ? candidates.filter(candidate =>
        isQualifiedView ? candidate.is_disqualified : !candidate.is_disqualified
      )
    : [];

  const hasSelectedCandidate = !isObjectEmpty(selectedCandidate);
  const hasNoSteps = isArrayEmpty(job.steps);
  const stepName = job.steps?.find(step => step.id === Number(stepId))?.title;
  const authVote = useMemo(() => {
    let findedReview = {};
    Object.values(candidateReviews)
      ?.filter(review => review.stepId === selectedCandidate.selection_step)
      .forEach(review => {
        const authReview = Object.values(review.reviews).find(
          record => record.reviewer.id === auth.id
        );
        if (authReview) {
          findedReview = authReview;
        }
      });

    return findedReview || {};
  }, [candidateReviews, selectedCandidate, auth]);
  const navigationItems = useMemo(() => getNavItems(job), [job]);

  const toggleAddCandidatesManuallyDrawer = () =>
    setIsAddManuallyCandidatesDrawerOpened(prevIsOpened => !prevIsOpened);

  const toggleUploadCvDrawer = () =>
    setIsUploadCvDrawerOpened(prevIsOpened => !prevIsOpened);

  const handleTimelinePageChange = async ({
    newPage,
    isLoadMore,
    candidate,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      ...prepareJobDetailsQueryParams({
        search,
        isQualifiedView,
        ordering,
      }),
    };

    const query = parseDuplicateParameters(params);

    if (isLoadMore && decodeURIComponent(location.search) !== `?${query}`) {
      navigate(`/jobs/${jobId}/details/${stepId}/timeline/?${query}`, {
        replace: true,
      });
    }

    try {
      if (isLoadMore) {
        setIsTimelineLoading(true);
      }
      const data = await getCandidateTimeline(
        dispatch,
        jobId,
        candidate.id,
        { [PAGE]: newPage },
        isLoadMore
      );
      setHasLoadMoreTimeline(hasNextPage(data));
    } finally {
      if (isLoadMore) {
        setIsTimelineLoading(false);
      }
    }
    return Promise.resolve();
  };

  const getUpdatedData = useCallback(
    async (person, navigationSection) => {
      setIsLoadingData(true);
      switch (navigationSection) {
        case CANDIDATE_NAVIGATIONS.PROFILE: {
          setIsLoadingData(false);
          break;
        }
        case CANDIDATE_NAVIGATIONS.TIMELINE: {
          if (!isObjectEmpty(person)) {
            setTimelinePage(defaultPage);
            await handleTimelinePageChange({
              newPage: defaultPage,
              isLoadMore: false,
              candidate: person,
            });
            setIsLoadingData(false);
          }

          break;
        }
        case CANDIDATE_NAVIGATIONS.COMMENTS: {
          if (!isObjectEmpty(person)) {
            await getCandidateComments(dispatch, jobId, person.id);
            setIsLoadingData(false);
          }

          break;
        }
        case CANDIDATE_NAVIGATIONS.COMMUNICATION: {
          if (!isObjectEmpty(person)) {
            await getCandidateEmails(dispatch, jobId, person.id);
            setIsLoadingData(false);
          }

          break;
        }
        case CANDIDATE_NAVIGATIONS.REVIEWS: {
          if (!isObjectEmpty(person)) {
            const reviewsResponse = await getCandidateReviews(
              dispatch,
              jobId,
              person.id
            );

            setCandidateReviews(getReviews(reviewsResponse, hiringTeam));
            setIsLoadingData(false);
          }

          break;
        }
        default: {
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navSection]
  );

  const navigateToProfileTab = ({ newView, newSearch, newOrdering } = {}) => {
    const params = prepareJobDetailsQueryParams({
      search: newSearch,
      isQualifiedView: newView,
      ordering: newOrdering,
    });
    const query = parseDuplicateParameters(params);

    navigate(`/jobs/${jobId}/details/${stepId}/profile/?${query}`, {
      replace: true,
    });
  };

  const navigateToCurrentTab = ({ newView, newSearch, newOrdering } = {}) => {
    const params = prepareJobDetailsQueryParams({
      search: newSearch,
      isQualifiedView: newView,
      ordering: newOrdering,
    });
    const query = parseDuplicateParameters(params);

    navigate(`/jobs/${jobId}/details/${stepId}/${navSection}/?${query}`, {
      replace: true,
    });
  };

  const handleSelectCandidate = async (
    candidate,
    shouldUpdate = false,
    newView = '',
    section = navSection
  ) => {
    const isQualifiedViewLabel = isQualifiedView
      ? QUALIFIED_STATUS.DISQUALIFIED
      : QUALIFIED_STATUS.QUALIFIED;
    const tempView = isEmpty(newView) ? newView : isQualifiedViewLabel;

    if (
      isObjectEmpty(selectedCandidate) ||
      selectedCandidate?.id !== candidate.id ||
      shouldUpdate
    ) {
      selectCandidateRequestRef.current += 1;
      const requestId = selectCandidateRequestRef.current;

      await http
        .get(
          api_selection_step_candidate(
            candidate?.step_id || candidate?.selection_step,
            candidate.id
          )
        )
        .then(async ({ data }) => {
          if (requestId !== selectCandidateRequestRef.current) return;

          setSelectedCandidate(data);
          getUpdatedData(data, section);
          if (
            tempView === QUALIFIED_STATUS.QUALIFIED &&
            section !== CANDIDATE_NAVIGATIONS.REVIEWS
          ) {
            getUpdatedData(data, CANDIDATE_NAVIGATIONS.REVIEWS);
          }
        });
    }
  };

  const getCandidateList = useCallback(
    async ({
      shouldUpdate,
      newSearch,
      newStatus,
      newView,
      newOrdering,
      section = CANDIDATE_NAVIGATIONS.PROFILE,
    } = {}) => {
      const isQualifiedViewLabel = newView
        ? QUALIFIED_STATUS.DISQUALIFIED
        : QUALIFIED_STATUS.QUALIFIED;
      const tempView = isEmpty(newStatus) ? newStatus : isQualifiedViewLabel;
      const tempStatus = tempView !== QUALIFIED_STATUS.QUALIFIED;

      await getCandidatesInStep(dispatch, jobId, {
        ...(newSearch ? { [SEARCH]: newSearch } : {}),
        ...(Number(stepId) ? { step_id: stepId } : {}),
        ...(newOrdering ? { [ORDERING]: newOrdering } : {}),
      }).then(data => {
        if (!shouldUpdate) {
          const person = data.find(
            personData => personData.is_disqualified === tempStatus
          );
          const params = prepareJobDetailsQueryParams({
            search: newSearch,
            isQualifiedView: tempStatus,
            ordering: newOrdering,
          });
          const query = parseDuplicateParameters(params);

          navigate(`/jobs/${jobId}/details/${stepId}/${section}/?${query}`, {
            replace: true,
          });

          if (person) {
            handleSelectCandidate(person, false, tempView, section);
          } else {
            setSelectedCandidate({});
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, jobId, stepId]
  );

  const getInitialData = useCallback(async () => {
    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.JOB_DETAILS
    );
    const initialSearch = params[SEARCH]
      ? decodeURIComponent(params[SEARCH])
      : '';
    const initialStatus = params[STATUS] ? params[STATUS] : defaultStatus;
    const initialOrdering = prepareValidCandidateOrdering(params[ORDERING]);
    const isQualified = initialStatus !== QUALIFIED_STATUS.QUALIFIED;
    const { data } = await http.get(api_job_team(jobId));
    setHiringTeam(data);
    setSearch(initialSearch);
    setOrdering(initialOrdering);
    setIsQualifiedView(isQualified);
    getCandidateList({
      shouldUpdate: false,
      newSearch: initialSearch,
      newStatus: initialStatus,
      newView: false,
      newOrdering: initialOrdering,
      section: navSection,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCandidateList]);

  const getJobData = useCallback(async () => {
    await Promise.all([
      getJob(dispatch, jobId),
      ...(canEdit ? [getAllCandidates(dispatch), getAllUsers(dispatch)] : []),
    ]);
    setIsLoading(false);
  }, [canEdit, dispatch, jobId]);

  useEffect(() => {
    getJobData();

    return () => {
      dispatch(clearCandidates());
      dispatch(clearAllUsers());
      dispatch(clearAllCandidates());
      dispatch(clearJob());
      dispatch(clearReviews());
      dispatch(clearComments());
      dispatch(clearTimeline());
    };
  }, [dispatch, getJobData]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    const newStatus = paramsStatus !== QUALIFIED_STATUS.QUALIFIED;
    const nextOrdering = prepareValidCandidateOrdering(paramsOrdering);

    if (
      paramsSearch !== null &&
      paramsStatus !== null &&
      (paramsSearch !== search ||
        newStatus !== isQualifiedView ||
        nextOrdering !== ordering)
    ) {
      if (paramsSearch !== search) {
        setSearch(paramsSearch);
      }

      if (newStatus !== isQualifiedView) {
        setIsQualifiedView(newStatus);
      }

      if (nextOrdering !== ordering) {
        setOrdering(nextOrdering);
      }
      const person = candidates.find(
        personData => personData.is_disqualified === newStatus
      );

      navigateToCurrentTab({
        newView: newStatus,
        newSearch: paramsSearch,
        newOrdering: nextOrdering,
      });

      if (person) {
        handleSelectCandidate(person, false, paramsStatus);
      } else {
        setSelectedCandidate({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    getUpdatedData(selectedCandidate, navSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUpdatedData, navSection]);

  const candidateNavigationItems = useMemo(() => {
    const params = prepareJobDetailsQueryParams({
      search,
      isQualifiedView,
      ordering,
    });

    const query = parseDuplicateParameters(params);

    return getCandidateNavigationItems(jobId, stepId, query);
  }, [jobId, stepId, search, isQualifiedView, ordering]);

  const navigateToCommunicationTab = () => {
    const isTimelineTab = navSection === CANDIDATE_NAVIGATIONS.TIMELINE;

    if (!isTimelineTab) {
      const params = prepareJobDetailsQueryParams({
        search,
        isQualifiedView,
        ordering,
      });

      const query = parseDuplicateParameters(params);

      navigate(`/jobs/${jobId}/details/${stepId}/communication/?${query}`, {
        replace: true,
      });
    } else {
      getUpdatedData(selectedCandidate, CANDIDATE_NAVIGATIONS.TIMELINE);
    }
  };

  const navigateToReviewTab = () => {
    const isTimelineTab = navSection === CANDIDATE_NAVIGATIONS.TIMELINE;

    if (!isTimelineTab) {
      const params = prepareJobDetailsQueryParams({
        search,
        isQualifiedView,
        ordering,
      });

      const query = parseDuplicateParameters(params);
      navigate(`/jobs/${jobId}/details/${stepId}/reviews/?${query}`, {
        replace: true,
      });
    } else {
      getUpdatedData(selectedCandidate, CANDIDATE_NAVIGATIONS.TIMELINE);
    }
  };

  const navigateToCommentsTab = () => {
    const isTimelineTab = navSection === CANDIDATE_NAVIGATIONS.TIMELINE;
    if (!isTimelineTab) {
      const params = prepareJobDetailsQueryParams({
        search,
        isQualifiedView,
        ordering,
      });

      const query = parseDuplicateParameters(params);

      navigate(`/jobs/${jobId}/details/${stepId}/comments/?${query}`, {
        replace: true,
      });
    } else {
      getUpdatedData(selectedCandidate, CANDIDATE_NAVIGATIONS.TIMELINE);
    }
  };

  const handleCreateTeam = () => {
    setIsHiringTeamDrawerOpened(true);
  };

  const onSearch = async text => {
    const searchTerm = trimString(text);

    setSearch(searchTerm);
    getCandidateList({
      shouldUpdate: false,
      newSearch: searchTerm,
      newStatus: '',
      newView: isQualifiedView,
      newOrdering: ordering,
    });
  };

  const onOrderingChange = value => {
    setOrdering(value);
    getCandidateList({
      shouldUpdate: false,
      newSearch: search,
      newStatus: '',
      newView: isQualifiedView,
      newOrdering: value,
    });
  };

  const handleCloseHiringTeamDrawer = () => {
    setIsHiringTeamDrawerOpened(false);
  };

  const handleSaveHiringTeam = async ({ hiring_team }) => {
    await http
      .patch(api_job_team(job.id), {
        hiring_team: getObjectToNumberArray(hiring_team),
      })
      .then(({ data }) => {
        setHiringTeam(data);
        showSuccessMessage(translations.successMessages.hiringTeamSaved);
        if (!isObjectEmpty(selectedCandidate)) {
          setCandidateReviews(getReviews(reviews, data));
        }
      });
  };

  const handleAddExistingCandidates = async () => {
    const { data } = await http.get(api_candidates_in_job(jobId));
    setCandidatesForJob(data);
    setIsAddExistingCandidatesDrawerOpened(true);
  };

  const handleAddExistingCandidatesCloseDrawer = () => {
    setIsAddExistingCandidatesDrawerOpened(false);
    setCandidatesForJob([]);
  };

  const handleAddExistingCandidatesSave = async data => {
    await http
      .patch(api_candidates_in_job(jobId), {
        candidates: getObjectToNumberArray(data.candidates),
      })
      .then(() => {
        showSuccessMessage(
          translations.successMessages.existingCandidatesSaved
        );
        getJob(dispatch, jobId);
        getCandidateList({
          shouldUpdate: false,
          newSearch: '',
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
        });
      });
  };

  const handleQualifyDialog = () => {
    setIsQualifyDialogOpened(true);
  };

  const handleCloseQualifyDialog = () => {
    setIsQualifyDialogOpened(false);
  };

  const handleQualifyCandidate = async () => {
    await http
      .patch(
        api_candidate_qualify(
          selectedCandidate.selection_step,
          selectedCandidate.id
        )
      )
      .then(() => {
        showSuccessMessage(
          translations.successMessages.candidateSuccessfullyQualified
        );
        getJob(dispatch, jobId);
        setIsQualifyDialogOpened(false);
        getCandidateList({
          shouldUpdate: false,
          newSearch: '',
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
          section: navSection,
        });
      });
  };

  const handleMoveToStep = async newStep => {
    await http
      .patch(api_add_candidate_to_step(newStep.id, selectedCandidate.id))
      .then(() => {
        showSuccessMessage(
          translations.successMessages.candidateMoveToStepSaved
        );
        getJob(dispatch, jobId);
        getCandidateList({
          shouldUpdate: false,
          newSearch: search,
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
          section: navSection,
        });
      });
  };

  const handleDisqualifyDialog = () => {
    setIsDisqualifyDialogOpened(true);
  };

  const handleCloseDisqualifyDialog = () => {
    setIsDisqualifyDialogOpened(false);
  };

  const handleDisqualify = async () => {
    await http
      .patch(
        api_candidate_disqualify(
          selectedCandidate.selection_step,
          selectedCandidate.id
        )
      )
      .then(() => {
        showSuccessMessage(
          translations.successMessages.candidateSuccessfullyDisqualified
        );
        setIsDisqualifyDialogOpened(false);
        getJob(dispatch, jobId);
        getCandidateList({
          shouldUpdate: false,
          newSearch: '',
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
          section: navSection,
        });
      });
  };

  const handleEditDrawer = () => {
    setIsEditCandidate(true);
    toggleAddCandidatesManuallyDrawer();
  };

  const handleVoteDialog = vote => {
    setSelectedVote(vote);
    setIsVoteDrawerOpened(true);
  };

  const handleCloseVoteDrawer = () => {
    setIsVoteDrawerOpened(false);
    setSelectedVote({});
  };

  const handleSaveVote = async review => {
    const payload = {
      vote: review.vote,
      comment: review.comment,
      [CANDIDATE]: selectedCandidate.id,
      [SELECTION_STEP]: selectedCandidate.selection_step,
    };

    if (isObjectEmpty(selectedVote)) {
      await http.post(api_candidate_reviews(jobId), payload).then(() => {
        showSuccessMessage(
          translations.successMessages.voteForCandidateSuccessfullySaved
        );
        dispatch(updateCandidateLastUpdate(selectedCandidate.id));
        navigateToReviewTab();
        if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
          getUpdatedData(selectedCandidate, navSection);
        }
      });
    } else {
      await http
        .patch(api_candidate_review(jobId, selectedVote.id), payload)
        .then(() => {
          showSuccessMessage(
            translations.successMessages.voteForCandidateSuccessfullyUpdated
          );
          dispatch(updateCandidateLastUpdate(selectedCandidate.id));
          setSelectedVote({});
          navigateToReviewTab();
          if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
            getUpdatedData(selectedCandidate, navSection);
          }
        });
    }
  };

  const handleEmailClick = () => {
    setIsSendEmailDrawerOpened(true);
  };

  const handleCloseSendEmailDrawer = () => {
    setIsSendEmailDrawerOpened(false);
  };

  const handleSendEmail = async emailData => {
    const payload = {
      ...emailData,
      [CANDIDATE]: selectedCandidate.id,
      [SELECTION_STEP]: selectedCandidate.selection_step,
    };

    await http.post(api_candidate_emails(jobId), payload).then(() => {
      showSuccessMessage(
        translations.successMessages.emailForCandidateSuccessfullySaved
      );
      dispatch(updateCandidateLastUpdate(selectedCandidate.id));
      navigateToCommunicationTab();
      if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
        getUpdatedData(selectedCandidate, navSection);
      }
    });
  };

  const handleAddComment = () => {
    setIsAddCommentDrawerOpened(true);
  };

  const handleCloseAddCommentDrawer = () => {
    setIsAddCommentDrawerOpened(false);
    setSelectedComment({});
  };

  const handleSaveComment = async commentData => {
    const payload = {
      content: commentData.content,
      [CANDIDATE]: selectedCandidate.id,
      [SELECTION_STEP]: selectedCandidate.selection_step,
    };

    if (isObjectEmpty(selectedComment)) {
      await http.post(api_candidate_comments(jobId), payload).then(() => {
        showSuccessMessage(
          translations.successMessages.commentForCandidateSuccessfullySaved
        );
        dispatch(updateCandidateLastUpdate(selectedCandidate.id));
        navigateToCommentsTab();
        if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
          getUpdatedData(selectedCandidate, navSection);
        }
      });
    } else {
      await http
        .patch(api_candidate_comment(jobId, selectedComment.id), payload)
        .then(() => {
          showSuccessMessage(
            translations.successMessages.commentForCandidateSuccessfullyUpdated
          );
          dispatch(updateCandidateLastUpdate(selectedCandidate.id));
          setSelectedComment({});
          navigateToCommentsTab();
          if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
            getUpdatedData(selectedCandidate, navSection);
          }
        });
    }
  };

  const handleSaveCandidateManually = async candidate => {
    const formData = new FormData();
    const { cv, parse_cv_data, ...candidateData } = candidate;
    const initialStep = job.steps.find(step => step.order === 1);

    formData.append('cv', cv || '');
    formData.append('parse_cv_data', parse_cv_data || false);

    await http.post(API_CANDIDATES, candidateData).then(candidateInfo => {
      http
        .patch(api_candidate(candidateInfo.data.id), formData)
        .then(({ data }) => {
          http
            .patch(api_add_candidate_to_step(initialStep.id, data.id))
            .then(() => {
              showSuccessMessage(translations.successMessages.candidateSaved);
              getAllCandidates(dispatch);
              getJob(dispatch, jobId);
              getCandidateList({
                shouldUpdate: false,
                newSearch: '',
                newStatus: '',
                newView: isQualifiedView,
                newOrdering: ordering,
              });
            });
        });
    });
  };

  const handleEditCandidate = async candidate => {
    const { cv, ...candidateData } = candidate;
    if (isObject(cv) || !cv) {
      const { parse_cv_data, ...data } = candidateData;
      const formData = new FormData();
      formData.append('cv', cv || '');
      formData.append('parse_cv_data', parse_cv_data || false);

      await http.patch(api_candidate(candidate.id), data).then(() => {
        http.patch(api_candidate(candidate.id), formData).then(() => {
          showSuccessMessage(translations.successMessages.candidateSaved);
          setIsEditCandidate(false);
          getCandidateList({
            shouldUpdate: true,
            newSearch: search,
            newStatus: '',
            newView: isQualifiedView,
            newOrdering: ordering,
          });
          navigateToProfileTab({
            newView: isQualifiedView,
            newSearch: search,
            newOrdering: ordering,
          });
          handleSelectCandidate(selectedCandidate, true);
        });
      });
    } else {
      await http.patch(api_candidate(candidate.id), candidateData).then(() => {
        showSuccessMessage(translations.successMessages.candidateSaved);
        setIsEditCandidate(false);
        getCandidateList({
          shouldUpdate: true,
          newSearch: search,
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
        });
        navigateToProfileTab({
          newView: isQualifiedView,
          newSearch: search,
          newOrdering: ordering,
        });
        handleSelectCandidate(selectedCandidate, true);
      });
    }
  };

  const handleDeleteCandidate = () => {
    setIsDeleteCandidateDialogOpened(true);
  };

  const handleCloseDelete = () => {
    setIsDeleteCandidateDialogOpened(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await http.delete(api_candidate(selectedCandidate.id));
      showSuccessMessage(translations.deleteCandidateDialog.deleteSuccess);
      setIsEditCandidate(false);
      getAllCandidates(dispatch);
      getJob(dispatch, jobId);
      getCandidateList({
        shouldUpdate: false,
        newSearch: '',
        newStatus: '',
        newView: isQualifiedView,
        newOrdering: ordering,
      });
    } finally {
      setIsDeleteCandidateDialogOpened(false);
      toggleAddCandidatesManuallyDrawer();
    }
  };

  const handleChangeView = () => {
    const newView = !isQualifiedView;
    const newStatus = newView
      ? QUALIFIED_STATUS.DISQUALIFIED
      : QUALIFIED_STATUS.QUALIFIED;
    setIsQualifiedView(prevState => !prevState);

    const person = candidates.find(
      personData => personData.is_disqualified === newView
    );

    navigateToCurrentTab({
      newView,
      newSearch: search,
      newOrdering: ordering,
    });
    if (person) {
      handleSelectCandidate(person, false, newStatus);
    } else {
      setSelectedCandidate({});
    }
  };

  const handleDeleteCommentDialog = comment => {
    setSelectedComment(comment);
    setIsDeleteCommentDialogOpened(true);
  };

  const handleCloseDeleteCommentDialog = () => {
    setIsDeleteCommentDialogOpened(false);
  };

  const handleDeleteComment = async () => {
    try {
      await http
        .delete(api_candidate_comment(jobId, selectedComment.id))
        .then(() => {
          showSuccessMessage(translations.deleteCommentDialog.deleteSuccess);
          dispatch(updateCandidateLastUpdate(selectedCandidate.id));
          setSelectedComment({});
          navigateToCommentsTab();

          if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
            getUpdatedData(selectedCandidate, navSection);
          }
        });
    } finally {
      handleCloseDeleteCommentDialog();
    }
  };

  const handleDeleteVoteDialog = review => {
    setSelectedVote(review);
    setIsDeleteVoteDialogOpened(true);
  };

  const handleCloseDeleteVoteDialog = () => {
    setIsDeleteVoteDialogOpened(false);
  };

  const handleDeleteVote = async () => {
    try {
      await http
        .delete(api_candidate_review(jobId, selectedVote.id))
        .then(() => {
          showSuccessMessage(translations.deleteVoteDialog.deleteSuccess);
          dispatch(updateCandidateLastUpdate(selectedCandidate.id));
          setSelectedVote({});
          navigateToReviewTab();
          if (navSection !== CANDIDATE_NAVIGATIONS.TIMELINE) {
            getUpdatedData(selectedCandidate, navSection);
          }
        });
    } finally {
      handleCloseDeleteVoteDialog();
    }
  };

  const handleEditComment = comment => {
    setSelectedComment(comment);
    setIsAddCommentDrawerOpened(true);
  };

  const handleLoadMore = () => {
    setTimelinePage(timelinePage + 1);
    return handleTimelinePageChange({
      newPage: timelinePage + 1,
      isLoadMore: true,
      candidate: selectedCandidate,
    });
  };

  const handleSaveUploadCv = async data => {
    try {
      const formData = new FormData();
      formData.append('cv', data.cv || '');
      formData.append('first_name', data.first_name || '');
      formData.append('last_name', data.last_name || '');
      formData.append('selection_step', job.steps?.[0]?.id);

      return http.post(API_CANDIDATES_FROM_CV, formData).then(() => {
        getCandidateList({
          shouldUpdate: false,
          newSearch: search,
          newStatus: '',
          newView: isQualifiedView,
          newOrdering: ordering,
        });
        showSuccessMessage(translations.cvUploadedSuccessfullyMessage);
      });
    } catch {
      showErrorMessage(translations.uploadCvErrorMessage);
    }
  };

  const getNotificationMessage = () => {
    const view = isQualifiedView
      ? QUALIFIED_STATUS.DISQUALIFIED
      : QUALIFIED_STATUS.QUALIFIED;
    const message = Number(stepId)
      ? getEmptyStepMessage(
          translations.noResults.message,
          stepName?.toLowerCase(),
          view
        )
      : translations.noResults.noStepsMessage;

    return isEmpty(search)
      ? translations.noResults.noSearchResultsMessage
      : message;
  };

  const onGoToJobDetails = () => {
    navigate(`/jobs/${jobId}/posting`);
  };

  const renderReportHeader = () => {
    const info = `${job?.department?.name || ''} ${
      job?.department?.name ? '·' : ''
    } ${job.workspace} ${job.location ? '·' : ''} ${job.location || ''}`;
    return (
      <div className={classes.header}>
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip
            className={classes.title}
            variant="h1"
            text={job.title}
          />
          <TextBoxWithTooltip variant="body2" text={info} />
        </div>
        {canEdit && (
          <div className={classes.actions}>
            <CustomButton
              className={classNames(classes.detailsButton)}
              type="withTextLightRounded"
              onClick={onGoToJobDetails}
            >
              {translations.viewJobPostingLabel}
            </CustomButton>
            <CustomButton
              className={classNames(classes.addMemberButton, classes.button, {
                [classes.disabledContainer]: isArchivedJob,
              })}
              onClick={handleCreateTeam}
              type="groupPeopleDarkRoundedOutlined"
            >
              {`${translations.addTeamMemberLabel} (${
                hiringTeam?.length || 0
              })`}
            </CustomButton>
            <DropdownMenu
              className={classes.dropdownMenu}
              menuItems={getCandidateActionItems(
                translations.candidateMenuItemLabels,
                job,
                handleAddExistingCandidates,
                toggleAddCandidatesManuallyDrawer,
                toggleUploadCvDrawer
              )}
              buttonLabel={translations.dropdownMenuLabel}
              isDisabled={hasNoSteps || isArchivedJob}
              disabledTooltipText={
                isArchivedJob ? '' : translations.disabledAddCandidateAction
              }
            />
          </div>
        )}
      </div>
    );
  };

  return (
    !isLoading && (
      <PageContainer
        {...rest}
        mainClass={classes.pageContainer}
        navigate={navigate}
        translations={translations}
        location={location}
        auth={auth}
        accessControl={accessControl}
        headerProps={{
          headerWrapperClass: classes.pageHeader,
          renderTitle: renderReportHeader,
          customNavigationClass: classes.navigation,
          customScrollArrowClass: classes.scrollArrow,
        }}
        navigationItems={navigationItems}
        shouldPassProps={false}
        shouldRemoveSpacing
        isFullWidthContent
      >
        <div className={classes.root}>
          <div className={classes.candidateList}>
            <ToggleButtonSwitch
              className={classes.switch}
              customButtonClass={classes.switchButton}
              value={isQualifiedView}
              items={getSwitchItems(translations, candidates, hasCandidates)}
              onSelect={handleChangeView}
            />
            <div className={classes.searchRow}>
              <Search
                className={classes.search}
                placeholder={translations.searchPlaceholder}
                value={search}
                onChange={onSearch}
              />
              <Filter
                className={classes.orderingFilter}
                customPaperClass={classes.orderingPaper}
                isOrdering
                options={prepareCandidateOrderingOptions(translations)}
                value={ordering}
                onChange={onOrderingChange}
              />
            </div>
            {isArray(candidatesList) && !isArrayEmpty(candidatesList) ? (
              <CandidatesList
                translations={translations.candidates}
                candidateClass={classes.candidate}
                users={candidatesList}
                selectedCandidateId={selectedCandidate?.id}
                onSelect={handleSelectCandidate}
                hasLabel
              />
            ) : (
              <NotificationCard
                className={classes.noCandidatesCard}
                titleClass={classes.notificationTitleClass}
                customIcon={NeutralIcon}
                title={translations.noResults.title}
                message={getNotificationMessage()}
                hasIcon
              />
            )}
          </div>
          {hasSelectedCandidate ? (
            <CandidateInfo
              translations={translations}
              user={selectedCandidate}
              auth={auth}
              canEdit={canEdit}
              authVote={authVote}
              steps={job.steps}
              candidateStep={selectedCandidate.selection_step}
              reviews={candidateReviews}
              hiringTeam={hiringTeam}
              comments={comments}
              timeline={timeline}
              emails={emails}
              navSection={navSection}
              candidateNavigationItems={candidateNavigationItems}
              isLoadingData={isLoadingData}
              isTimelineLoading={isTimelineLoading}
              hasLoadMoreTimeline={hasLoadMoreTimeline}
              isArchivedJob={isArchivedJob}
              isOngoingCvParsing={selectedCandidate?.ongoing_cv_parsing}
              handleMoveToStep={handleMoveToStep}
              handleDisqualify={handleDisqualifyDialog}
              handleQualify={handleQualifyDialog}
              handleAddComment={handleAddComment}
              handleEmailClick={handleEmailClick}
              handleVote={handleVoteDialog}
              handleEditDrawer={handleEditDrawer}
              handleEditComment={handleEditComment}
              handleDeleteComment={handleDeleteCommentDialog}
              handleDeleteVote={handleDeleteVoteDialog}
              onLoadMore={handleLoadMore}
            />
          ) : (
            <NotificationCard
              className={classes.noSelectedCandidateCard}
              customIcon={TimerIcon}
              title={translations.noResults.waitingForCandidatesTitle}
              hasIcon
            />
          )}
        </div>
        <CustomFormDrawer
          customContentClass={classes.drawerContent}
          translations={translations.manageHiringTeam}
          isOpened={isHiringTeamDrawerOpened}
          initialData={prepareHiringTeamInitialData(hiringTeam)}
          fields={prepareHiringTeamFields(classes)}
          allUsers={allUsers}
          onClose={handleCloseHiringTeamDrawer}
          onSave={handleSaveHiringTeam}
          hideDelete
          hasCancelButton
          isInitialValid
        />
        <CustomFormDrawer
          customContentClass={classes.drawerContent}
          translations={translations.manageAddExistingCandidates}
          isOpened={isAddExistingCandidatesDrawerOpened}
          initialData={prepareCandidatesInitialData(candidatesForJob)}
          fields={prepareAddCandidatesFields(classes)}
          allCandidates={allCandidates}
          onClose={handleAddExistingCandidatesCloseDrawer}
          onSave={handleAddExistingCandidatesSave}
          hideDelete
          hasCancelButton
          isInitialValid
        />
        <CustomFormDrawer
          customFormClass={classes.form}
          translations={translations.addCandidatesManually}
          isOpened={isAddManuallyCandidatesDrawerOpened}
          initialData={prepareCandidateInitialData(
            isEditCandidate ? selectedCandidate : {}
          )}
          fields={getCandidateFields(classes)}
          onClose={() => {
            setIsEditCandidate(false);
            toggleAddCandidatesManuallyDrawer();
          }}
          onSave={candidate =>
            isEditCandidate
              ? handleEditCandidate(candidate)
              : handleSaveCandidateManually(candidate)
          }
          hideDelete={!isEditCandidate}
          onDelete={handleDeleteCandidate}
          hasCancelButton
          isInitialValid
          isMiddleWidth
        />
        <CustomFormDrawer
          translations={translations.addCandidatesUsingCv}
          isOpened={isUploadCvDrawerOpened}
          initialData={UPLOAD_CV_INITIAL_DATA}
          fields={getUploadCvFields()}
          onClose={toggleUploadCvDrawer}
          onSave={handleSaveUploadCv}
          hideDelete
          hasCancelButton
        />
        <CustomFormDrawer
          translations={translations.sendEmail}
          isOpened={isSendEmailDrawerOpened}
          initialData={getSendEmailInitialData()}
          fields={GET_SEND_EMAIL_FIELDS}
          onClose={handleCloseSendEmailDrawer}
          onSave={handleSendEmail}
          hideDelete
          hasCancelButton
        />
        <CustomFormDrawer
          translations={translations.addComment}
          isOpened={isAddCommentDrawerOpened}
          initialData={getAddCommentInitialData(selectedComment)}
          fields={getAddCommentFields(classes)}
          onClose={handleCloseAddCommentDrawer}
          onSave={handleSaveComment}
          onDelete={() => setIsDeleteCommentDialogOpened(true)}
          isInitialValid={!isObjectEmpty(selectedComment)}
          hasCancelButton
        />
        <CustomFormDrawer
          translations={translations.vote}
          isOpened={isVoteDrawerOpened}
          initialData={getVoteInitialData(selectedVote)}
          fields={getVoteFields()}
          onClose={handleCloseVoteDrawer}
          onSave={handleSaveVote}
          isInitialValid={!isObjectEmpty(selectedVote)}
          onDelete={() => setIsDeleteVoteDialogOpened(true)}
          hasCancelButton
        />
        <AlertDialog
          translations={translations.disqualifyCandidateDialog}
          isOpened={isDisqualifyDialogOpened}
          onClose={handleCloseDisqualifyDialog}
          onConfirm={handleDisqualify}
          isWarning
        />
        <AlertDialog
          translations={translations.qualifyCandidateDialog}
          isOpened={isQualifyDialogOpened}
          onClose={handleCloseQualifyDialog}
          onConfirm={handleQualifyCandidate}
          isWarning
        />
        <AlertDialog
          translations={translations.deleteCandidateDialog}
          isOpened={isDeleteCandidateDialogOpened}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
          isWarning
        />
        <AlertDialog
          translations={translations.deleteVoteDialog}
          isOpened={isDeleteVoteDialogOpened}
          onClose={handleCloseDeleteVoteDialog}
          onConfirm={handleDeleteVote}
          isWarning
        />
        <AlertDialog
          translations={translations.deleteCommentDialog}
          isOpened={isDeleteCommentDialogOpened}
          onClose={handleCloseDeleteCommentDialog}
          onConfirm={handleDeleteComment}
          isWarning
        />
      </PageContainer>
    )
  );
};

JobDetailsPage.propTypes = {
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(JobDetailsPage);
